import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Badge from 'components/base/Badge';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import FeatherIcon from 'feather-icons-react';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useState } from 'react';
import axios from 'axios';
import { errorHandler } from 'helpers/response-utils';
import { toast } from 'react-toastify';

interface DietitianTableProps {
  clinicId: string | undefined;
  onDeleteSuccess?: () => void;
  onEditDietitian?: (dietitian: any) => void;
}

export const DietitianTableColumns = (
  clinicId: string | undefined,
  onDeleteSuccess?: () => void,
  onEditDietitian?: (dietitian: any) => void
): ColumnDef<any>[] => [
  {
    accessorKey: 'firstname',
    header: 'Name',
    cell: ({ row: { original } }) => {
      const { firstname } = original;
      return (
        <div className="d-flex align-items-center">
          <div>
            <span className="fs-8 fw-bold">{firstname}</span>
          </div>
        </div>
      );
    },
    meta: {
      headerProps: {
        style: { width: '25%' },
        className: 'ps-4 pe-5 border-start border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap ps-4 pe-5 border-start border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row: { original } }) => {
      const { email } = original;
      return (
        <a className="text-body-highlight" href={`mailto:${email}`}>
          {email}
        </a>
      );
    },
    meta: {
      headerProps: {
        style: { width: '30%', minWidth: '200px' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-4 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Date',
    cell: ({ row: { original } }) => original.createdAt,
    meta: {
      headerProps: {
        style: { width: '20%' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap ps-4 text-body-tertiary border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row: { original } }) =>
      String(original.role || '').toUpperCase() === 'CLINIC'
        ? 'Clinic Admin'
        : 'Dietitian',
    meta: {
      headerProps: {
        style: { width: '15%' },
        className: 'ps-4 pe-5 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap ps-4 text-body-tertiary border-end border-translucent'
      }
    }
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row: { original } }) => {
      const token = localStorage.getItem('token');
      const [show, setShow] = useState(false);

      const handleClose = () => setShow(false);
      const handleShow = () => setShow(true);

      const deleteDietitian = async (dietitianId: string) => {
        try {
          const response = await axios.delete(
            `${process.env.REACT_APP_ENDPOINT}/clinic/${clinicId}/dietitians/${dietitianId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (response.data['status'] == false) {
            errorHandler(response.data);
            return false;
          }

          toast.success('Dietitian deleted successfully!');
          handleClose();
          if (onDeleteSuccess) {
            onDeleteSuccess();
          }
        } catch (error) {
          console.error('Error deleting dietitian:', error);
          toast.error('Failed to delete dietitian');
        }
      };

      const isClinicAdmin =
        String(original.role || '').toUpperCase() === 'CLINIC';

      return (
        <>
          <RevealDropdownTrigger>
            <RevealDropdown>
              <Dropdown.Item
                onClick={() => {
                  if (onEditDietitian) {
                    onEditDietitian(original);
                  }
                }}
              >
                Edit
              </Dropdown.Item>
              {!isClinicAdmin && (
                <Dropdown.Item onClick={handleShow} className="text-danger">
                  Remove
                </Dropdown.Item>
              )}
            </RevealDropdown>
          </RevealDropdownTrigger>

          <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header>
              <Modal.Title>Are you sure?</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to delete this user?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  deleteDietitian(original.id);
                }}
              >
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'text-end border-end pe-4 ps-4 border-translucent'
      },
      cellProps: {
        className: 'text-end border-end pe-4 ps-4 border-translucent'
      }
    }
  }
];

const DietitianTable = ({
  clinicId,
  onDeleteSuccess,
  onEditDietitian
}: DietitianTableProps & { onEditDietitian?: (dietitian: any) => void }) => {
  return (
    <div className="border-top border-translucent">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination={false} className="py-4" />
    </div>
  );
};

export default DietitianTable;
